<template>
    <div class="cart-item flex items-start space-x-4 mt-3">
        <!-- Product Image -->
        <img :src="item.image" alt="Product image" class="item-image w-16 h-16 object-cover rounded-lg shadow-sm" />

        <!-- Item Details -->
        <div class="item-details flex-1">
            <!-- Item Name, Package Discount, and Price -->
            <div class="flex justify-between items-start mb-2">
                <h4 class="font-medium text-base text-gray-800 break-words mr-2">
                    {{ item.name }}
                    <span v-if="item.type === 'package' && item.value > 0"
                        class="bg-red-900 text-white text-xs font-medium ml-2 px-2.5 py-0.5 rounded">
                        - {{ item.value }}%
                    </span>
                </h4>
                <div class="flex items-center space-x-2">
                    <!-- Free Shipping -->
                    <p v-if="item.type === 'shipping' && item.free_shipping" class="text-red-500 line-through">
                        {{ item.price?.formatted || 'N/A' }}
                    </p>
                    <strong v-if="item.type === 'shipping' && item.free_shipping" class="text-green-500">
                        {{ $translate('FREE SHIPPING') }}
                    </strong>
                    <!-- Price -->
                    <p v-else class="text-gray-600 text-sm whitespace-nowrap">
                        {{ item.price?.formatted || 'N/A' }}
                    </p>
                </div>
            </div>

            <!-- Excerpt or Notes -->
            <div v-if="item.excerpt" class="text-gray-600 text-sm mb-2">
                {{ item.excerpt }}
            </div>

            <!-- Shipping Note -->
            <div v-if="item.type === 'shipping'" class="cart-note p-2 bg-yellow-100 rounded mt-1">
                <p class="text-gray-600 text-sm">
                    {{ $translate('Shipping costs are applied only if any items require shipping.') }}
                </p>
            </div>

            <!-- Package Discount Note -->
            <div v-if="item.type === 'package' && item.value > 0" class="cart-note p-2 bg-yellow-100 rounded mt-1">
                <p class="text-gray-600 text-sm">
                    {{ $translate('Package discount %s% are subtracted from the product prices before VAT.',
                        [item.value]) }}
                </p>
            </div>

            <!-- Quantity Controls and Remove Button -->
            <div v-if="item.allow.quantity || item.allow.delete" class="flex justify-between items-center mt-4">
                <div class="flex items-center space-x-4">
                    <!-- Quantity Controls -->
                    <div v-if="item.allow.quantity" class="quantity-control flex items-center">
                        <button @click="updateQuantity(item.id, item.quantity + 1)"
                            :disabled="disabled"
                            class="bg-blue-500 text-white h-8 w-8 flex items-center justify-center rounded-l-md disabled:bg-gray-300">
                            <FontAwesomeIcon icon="plus" />
                        </button>
                        <span class="h-8 w-8 flex items-center justify-center border border-gray-300">
                            {{ item.quantity }}
                        </span>
                        <span v-if="item.unit" class="h-8 w-12 flex items-center justify-center border border-gray-300">
                            {{ item.unit }}
                        </span>
                        <button @click="updateQuantity(item.id, item.quantity - 1)" :disabled="item.quantity <= 1 || disabled"
                            class="bg-blue-500 text-white h-8 w-8 flex items-center justify-center rounded-r-md disabled:bg-gray-300">
                            <FontAwesomeIcon icon="minus" />
                        </button>
                    </div>
                </div>

            </div>

            <!-- Add Attendee / Add Addon -->
            <div class="flex space-x-4">
                <div v-if="item.allow.contact && !disabled" class="flex items-center">
                    <button @click="openContactModal(item.id)" id="add-attendee-btn" class="text-blue-500 text-xs flex items-center add-attendee-button">
                        <FontAwesomeIcon icon="user-plus" class="mr-1" />
                        {{ $translate('Add Attendee') }}
                    </button>
                </div>
                <div v-if="item.allow.addon && !disabled" class="flex items-center">
                    <button @click="openAddonModal(item.id)" class="text-blue-500 text-xs flex items-center">
                        <FontAwesomeIcon icon="cart-plus" class="mr-1" />
                        {{ $translate('Add Addon') }}
                    </button>
                </div>
                <div v-if="item.allow.delete && !disabled" class="flex items-center">
                    <button @click="handleRemoveItem(item.id, item.type)"
                        class="text-red-500 text-xs flex items-center">
                        <FontAwesomeIcon icon="trash" class="mr-1" />
                        {{ $translate('Remove from cart') }}
                    </button>
                </div>
            </div>

            <!-- Attendees List -->
            <div v-if="item.contacts?.length" class="mt-4">
                <h6 class="font-semibold text-sm text-gray-700">{{ $translate('Attendees') }}:</h6>
                <ul class="text-gray-600">
                    <li v-for="contact in item.contacts" :key="contact.id"
                        class="flex justify-between text-xs items-center">
                        <span>{{ contact.data.full_name }} ({{ contact.data.email }})</span>
                        <!-- Adjusted Logic for Remove Button -->
                        <button v-if="item.contacts.length > 1 && item.allow.delete && !disabled"
                            @click="handleRemoveContact(item.id, contact.id)" class="text-red-500 text-xs">
                            <FontAwesomeIcon icon="trash" />
                        </button>
                    </li>
                </ul>
            </div>

            <!-- Included Items (for packages) -->
            <div v-if="item.children?.length" class="mt-4">
                <h5 class="font-semibold text-gray-700 text-base">{{ $translate('Included') }}:</h5>
                <div class="children mt-4 space-y-4">
                    <div v-for="child in item.children" :key="child.id"
                        class="child-item bg-white shadow-md rounded-lg p-4 border border-gray-200">
                        <!-- Reuse CartItem for each child -->
                        <CartItem :item="child" 
                            @update-quantity="updateQuantity" 
                            @item-removed="handleRemoveItem"
                            @contact-removed="handleRemoveContact" 
                            @voucher-removed="handleRemoveVoucher"
                            @package-removed="handleRemovePackage" 
                            @open-contact-modal="openContactModal"
                            @open-addon-modal="openAddonModal"
                            :disabled="disabled" />
                    </div>
                </div>
            </div>

            <!-- Vouchers List -->
            <div v-if="item.vouchers?.length" class="mt-4">
                <h6 class="font-semibold text-sm text-gray-700">{{ $translate('Savings') }}:</h6>
                <ul class="text-gray-600">
                    <li v-for="voucher in item.vouchers" :key="voucher.id" class="flex justify-between items-center">
                        <div class="flex flex-col">
                            <div class="flex items-center space-x-2">
                                <FontAwesomeIcon icon="tag" class="text-green-500" />
                                <span>{{ voucher.code }} ({{ voucher.name }})</span>
                            </div>
                            <span class="text-red-700">-{{ voucher.formatted || 'N/A' }}</span>
                        </div>
                        <button v-if="!disabled" @click="handleRemoveVoucher(item.id, voucher.id)" class="text-red-500 text-xs">
                            <FontAwesomeIcon icon="trash" />
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { inject } from 'vue';

export default {
    name: 'CartItem',
    components: {
        FontAwesomeIcon,
    },
    props: {
        item: {
            type: Object,
            required: true,
            default: () => ({
                children: [],
                vouchers: [],
                contacts: [],
            }),
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const cartStore = inject('cartStore');

        const updateQuantity = (itemId, quantity) => {
            if (quantity > 0) {
                emit('update-quantity', itemId, quantity);
            }
        };

        const handleRemoveItem = async (itemId, type) => {
            await cartStore.removeItem(itemId, type);
            emit('item-removed');
        };

        const handleRemoveContact = async (itemId, contactId) => {
            await cartStore.removeContact(itemId, contactId);
            emit('contact-removed');
        };

        const handleRemoveVoucher = async (itemId, voucherId) => {
            await cartStore.removeVoucher(itemId, voucherId);
            emit('voucher-removed');
        };

        const handleRemovePackage = async (itemId) => {
            await cartStore.removePackage(itemId);
            emit('package-removed');
        };

        const openContactModal = (itemId) => {
            emit('open-contact-modal', itemId);
        };

        const openAddonModal = (itemId) => {
            emit('open-addon-modal', itemId);
        };

        return {
            updateQuantity,
            handleRemoveItem,
            handleRemoveContact,
            handleRemoveVoucher,
            handleRemovePackage,
            openContactModal,
            openAddonModal,
        };
    },
};
</script>

<style scoped>
.cart-item {
    padding-bottom: 1rem;
    padding-top: 1rem;
}

.cart-item:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
}

.quantity-control button {
    min-width: 2rem;
}

.quantity-control span {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    min-width: 2rem;
    height: 2rem;
}

.children {
    display: flex;
    flex-direction: column;
}

.child-item {
    background-color: transparent;
    border-radius: 0.7rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid #ededed;
    padding-top: 0;
    padding-bottom: 0;
}
</style>